import { useState } from 'react'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { Banner, Button, ErrorState, Skeleton, StatusBadge } from '@shared/design-system/ui'
import { useAdminReceivingPlan } from '../api/admin-receiving-plan'
import { planName, planPercent } from '../model/user-receiving-plan'
import { PlanConditions } from './PlanConditions'
import { AdminReceivingPlanEditor } from './AdminReceivingPlanEditor'
import { ReserveSyncStatus } from './ReserveSyncStatus'
import * as styles from './receiving-plan.css'

export function AdminReceivingPlanPanel({
  userId,
  userLabel,
  canManage,
}: {
  userId: string
  userLabel: string
  canManage: boolean
}) {
  const customFees = useFeatureFlag('customServiceFees')
  const query = useAdminReceivingPlan(userId)
  const [editing, setEditing] = useState(false)
  if (query.isPending) return <Skeleton height={350} />
  if (query.isError)
    return (
      <ErrorState title="Não foi possível carregar o plano" onRetry={() => void query.refetch()} />
    )
  const { current, history } = query.data
  return (
    <div className={styles.stack} data-testid="admin-receiving-plan-panel">
      <section className={styles.section}>
        <div className={styles.header}>
          <div className={styles.stack}>
            <h2 className={styles.heading}>Plano e taxas</h2>
            <StatusBadge tone="success" label={planName(current)} />
          </div>
          {canManage ? (
            <Button data-testid="receiving-plan-edit-btn" onClick={() => setEditing(true)}>
              Editar plano e taxas
            </Button>
          ) : null}
        </div>
        <PlanConditions
          plan={current}
          reserveApplication={
            <ReserveSyncStatus
              key={`${userId}:${current.versionId}:${current.defaultVersionId}`}
              userId={userId}
              plan={current}
            />
          }
        />
        <Banner tone="info" title="Cada venda mantém suas condições">
          {customFees
            ? 'O novo plano vale para novas vendas e novas assinaturas após a confirmação da reserva. Assinaturas existentes mantêm as taxas e a reserva contratadas em todas as renovações. Cada participante do split mantém suas condições registradas. Recebíveis já gerados não são recalculados.'
            : 'A reserva muda após a confirmação da Stripe, para novas vendas e próximas cobranças de assinaturas. Cada participante do split segue seu próprio plano. Recebíveis já gerados mantêm as taxas, reservas e datas de liberação originais.'}
        </Banner>
      </section>
      <section className={`${styles.section} ${styles.stack}`} aria-label="Histórico do plano">
        <h2 className={styles.heading}>Histórico de alterações</h2>
        {history?.length ? (
          history.map((entry) => (
            <div className={styles.history} key={entry.versionId}>
              <div className={styles.header}>
                <strong>
                  {entry.kind === 'CUSTOM' ? 'Personalizado' : entry.receivingPeriod} ·{' '}
                  {entry.receivingDays} dias
                </strong>
                <time dateTime={entry.effectiveFrom}>
                  {new Date(entry.effectiveFrom).toLocaleString('pt-BR')}
                </time>
              </div>
              <p className={styles.copy}>{entry.reason}</p>
              {customFees && entry.serviceFee ? (
                <p
                  className={styles.copy}
                  data-testid={`receiving-plan-history-fee-${entry.versionId}`}
                >
                  Serviço VendePay: {planPercent(entry.serviceFee.percentage)} +{' '}
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' }).format(
                    Number(entry.serviceFee.fixedUsd),
                  )}{' '}
                  por cobrança. Novas vendas e novas assinaturas.
                </p>
              ) : null}
              <p className={styles.copy}>
                {entry.actorType === 'SELLER'
                  ? 'Alterado pelo vendedor'
                  : `Alterado por ${entry.actorName || entry.actorId}`}
              </p>
            </div>
          ))
        ) : (
          <p className={styles.copy}>Nenhuma alteração registrada.</p>
        )}
      </section>
      {editing && canManage ? (
        <AdminReceivingPlanEditor
          userId={userId}
          userLabel={userLabel}
          data={query.data}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </div>
  )
}
